import React, { FunctionComponent, useMemo, useState } from "react";
import { ACard, AChartX, AChartXProps, XAxis, YAxis } from "@atiautomacao/ati-ui-library";
import BatteryChargingFullOutlinedIcon from "@mui/icons-material/BatteryChargingFullOutlined";
import { Skeleton, Stack, TextField, useTheme } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { endOfDay, format, isWithinInterval, startOfDay } from "date-fns";
import { getChartXOptions } from "../../../Utils/ChartXOption";
import { LocalDateTimeFormatISO } from "../../../Utils/DateFormatPatternUtils";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import { useBessSoc } from "./BessReactQuery";
import { BessChartSeries } from "./BessChartTypes";
import { hasPoints, toTimeSeriesData } from "./BessChartUtils";

const SOC_COLOR = "#1BB483";
const EMPTY_SERIES: Array<BessChartSeries> = [];

const BessSocChart: FunctionComponent<{ equipmentId: number }> = ({ equipmentId }) => {
    const theme = useTheme();
    const [date, setDate] = useState<Date>(startOfDay(new Date()));

    const { data, isLoading } = useBessSoc(equipmentId, date);
    const series = data?.data ?? EMPTY_SERIES;

    // @ts-ignore
    const option: AChartXProps["option"] = useMemo(() => {
        const xAxis: XAxis = {
            type: "time",
            boundaryGap: false,
            axisLabel: {
                // @ts-ignore - echarts entrega timestamp
                formatter: (value: number) => format(new Date(value), "HH:mm:ss"),
            },
        };

        // Estado de Carga: escala fixa 0–100%, marcações de 25 em 25% (SGD-3676 Regra 4c)
        const yAxis: YAxis = {
            type: "value",
            min: 0,
            max: 100,
            interval: 25,
            axisLabel: { formatter: "{value}%" },
        };

        const chartSeries: any[] = series.map((s) => ({
            name: "SOC",
            type: "line" as const,
            smooth: false,        // sem interpolação (SGD-3676 Regra 4c)
            connectNulls: false,
            showSymbol: false,
            symbol: "circle",
            lineStyle: { width: 2, color: SOC_COLOR },
            itemStyle: { color: SOC_COLOR },
            areaStyle: { color: "rgba(27, 180, 131, 0.15)" },
            tooltip: { valueFormatter: (v: any) => (v != null ? `${v}%` : "-") },
            data: toTimeSeriesData(s),
        }));

        return {
            ...getChartXOptions({ name: `bess-soc-${equipmentId}-`, date }),
            legend: {
                show: true,
                bottom: 2,
                left: "center",
                data: ["SOC"],
            },
            grid: { containLabel: true, top: 15, bottom: 40, right: 15, left: 15 },
            toolbox: {
                feature: {
                    saveAsImage: { show: true },
                    dataZoom: { yAxisIndex: "none" },
                    restore: { show: true },
                },
            },
            dataZoom: [{ type: "inside", throttle: 50 }],
            xAxis: xAxis,
            yAxis: yAxis,
            series: chartSeries,
        };
    }, [series, date, equipmentId]);

    let content: JSX.Element;
    if (isLoading) {
        content = <Skeleton animation="wave" height={250} variant="rounded" width={"100%"} />;
    } else if (hasPoints(series)) {
        content = <AChartX height={250} option={option} theme={theme.palette.mode} />;
    } else {
        content = <DataNotFound />;
    }

    return (
        <ACard
            ariaLabel={"bess-soc-chart"}
            title={"Estado de carga (SOC)"}
            iconTitle={{
                showIcon: true,
                kind: "mui",
                iconMaterialComponent: BatteryChargingFullOutlinedIcon,
                iconTitleStyle: { color: "#2D5DE7", fontSize: 28 },
            }}
            headerControlsPosition={"header"}
            styleProps={{
                cardStyle: { height: "auto" },
                contentStyle: { padding: "0 16px 16px", alignItems: "center" },
                titleStyle: { alignItems: "center", display: "flex" },
                subTitleStyle: { display: "none" },
                headerStyle: { flexDirection: "row" },
                headerActionsStyle: { alignItems: "center", display: "flex" },
            }}
            headerControls={
                <Stack direction="row" sx={{ alignItems: "center", display: "flex", padding: "8px" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Data"
                            disableFuture
                            value={date}
                            inputFormat={"DD/MM/YYYY"}
                            renderInput={(params) => (
                                <TextField
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", height: "30px", width: "155px" } }}
                                    {...params}
                                />
                            )}
                            onChange={(newValue) => {
                                if (newValue && isWithinInterval(new Date(newValue), { start: new Date("1980-01-01"), end: endOfDay(new Date()) })) {
                                    setDate(startOfDay(new Date(LocalDateTimeFormatISO(newValue))));
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Stack>
            }
        >
            {content}
        </ACard>
    );
};

export default BessSocChart;
